import { useEffect, useState } from 'react';
import {
  cloneSchemaDocument,
  createEnum,
  createEnumValue,
  parseNullableInteger,
  slugify,
  toPascalCase,
} from './schema-editor-helpers';
import type { SchemaEditorCommonProps } from './schema-editor-types';
import { SchemaEditorActions, SchemaEmptySelection } from './SchemaEditorActions';

export function SchemaEnumsEditor(props: SchemaEditorCommonProps): React.JSX.Element {
  const [selectedId, setSelectedId] = useState(props.document.enums[0]?.id ?? '');
  const [search, setSearch] = useState('');

  useEffect(syncSelection, [props.document.enums, selectedId]);

  const selected = findSelectedEnum();
  const rows: React.JSX.Element[] = [];
  const normalizedSearch = search.trim().toLowerCase();
  for (const definition of props.document.enums) {
    if (normalizedSearch !== '' && !definition.displayName.toLowerCase().includes(normalizedSearch) && !definition.key.includes(normalizedSearch)) {
      continue;
    }
    rows.push(
      <button
        className={definition.id === selectedId ? 'schema-list-row active' : 'schema-list-row'}
        data-id={definition.id}
        key={definition.id}
        onClick={selectEnum}
        type="button"
      >
        <span><strong>{definition.displayName || 'Untitled enum'}</strong><small>{definition.csharpTypeName || 'Missing C# type'}</small></span>
        <code>{definition.values.length}</code>
      </button>,
    );
  }

  return (
    <section className="schema-editor-grid">
      <aside className="surface schema-definition-browser">
        <div className="surface-heading"><div><span className="eyebrow">Schema</span><h2>Enums</h2></div><strong>{props.document.enums.length}</strong></div>
        <input className="schema-search" placeholder="Search enums" value={search} onChange={changeSearch} />
        {props.canManage && <button className="secondary-button schema-add-button" onClick={addEnum} type="button">Add enum</button>}
        <div className="schema-list">{rows.length === 0 ? <p className="schema-empty-list">No enums found.</p> : rows}</div>
      </aside>

      <article className="surface schema-detail-editor schema-enum-editor">
        {selected === undefined ? <SchemaEmptySelection label="enum" /> : (
          <>
            <div className="surface-heading">
              <div><span className="eyebrow">Enum definition</span><h2>{selected.displayName || 'Untitled enum'}</h2></div>
              <code>{selected.id.slice(0, 8)}</code>
            </div>
            <div className="schema-form-grid">
              <label>Display name<input name="displayName" value={selected.displayName} onChange={changeDefinitionField} disabled={!props.canManage} /></label>
              <label>Technical key<input name="key" value={selected.key} onChange={changeDefinitionField} disabled={!props.canManage} spellCheck={false} /></label>
              <label>C# type name<input name="csharpTypeName" value={selected.csharpTypeName} onChange={changeDefinitionField} disabled={!props.canManage} spellCheck={false} /></label>
              <label className="schema-wide-field">Description<textarea name="description" value={selected.description} onChange={changeDefinitionField} disabled={!props.canManage} /></label>
            </div>

            <div className="schema-subsection-heading">
              <div><span className="eyebrow">Closed values</span><h3>Enum values</h3></div>
              {props.canManage && <button className="secondary-button" onClick={addValue} type="button">Add value</button>}
            </div>
            <div className="schema-enum-values">
              {renderValues(selected.id)}
            </div>
            {props.canManage && <SchemaEditorActions busy={props.busy} dirty={props.dirty} onDelete={deleteEnum} onReset={props.onReset} onSave={save} />}
          </>
        )}
      </article>
    </section>
  );

  function syncSelection(): void {
    if (selectedId !== '' && findEnumIndex(props, selectedId) >= 0) {
      return;
    }
    setSelectedId(props.document.enums[0]?.id ?? '');
  }

  function findSelectedEnum() {
    const index = findEnumIndex(props, selectedId);
    return index < 0 ? undefined : props.document.enums[index];
  }

  function renderValues(enumId: string): React.ReactNode {
    const definition = findSelectedEnum();
    if (definition === undefined || definition.values.length === 0) {
      return <p className="schema-empty-list">This enum has no values. It can be saved as a draft but cannot be published.</p>;
    }
    const valueRows: React.JSX.Element[] = [];
    for (const value of definition.values) {
      valueRows.push(
        <div className="schema-enum-value-row" key={value.id}>
          <label>Value<input data-enum-id={enumId} data-value-id={value.id} name="value" type="number" value={value.value ?? ''} onChange={changeValueField} disabled={!props.canManage} /></label>
          <label>Key<input data-enum-id={enumId} data-value-id={value.id} name="key" value={value.key} onChange={changeValueField} disabled={!props.canManage} spellCheck={false} /></label>
          <label>Display name<input data-enum-id={enumId} data-value-id={value.id} name="displayName" value={value.displayName} onChange={changeValueField} disabled={!props.canManage} /></label>
          <label className="schema-value-description">Description<input data-enum-id={enumId} data-value-id={value.id} name="description" value={value.description} onChange={changeValueField} disabled={!props.canManage} /></label>
          {props.canManage && <button className="schema-row-delete" data-value-id={value.id} onClick={deleteValue} type="button" aria-label={`Delete ${value.displayName}`}>×</button>}
        </div>,
      );
    }
    return valueRows;
  }

  function changeSearch(event: React.ChangeEvent<HTMLInputElement>): void {
    setSearch(event.target.value);
  }

  function selectEnum(event: React.MouseEvent<HTMLButtonElement>): void {
    setSelectedId(event.currentTarget.dataset.id ?? '');
  }

  function addEnum(): void {
    const document = cloneSchemaDocument(props.document);
    const definition = createEnum(document);
    document.enums.push(definition);
    props.onDocumentChange(document);
    setSelectedId(definition.id);
  }

  function changeDefinitionField(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const document = cloneSchemaDocument(props.document);
    const index = findEnumIndex({ document }, selectedId);
    if (index < 0) {
      return;
    }
    const definition = document.enums[index]!;
    const field = event.target.name;
    if (field === 'displayName') {
      const priorSuggestedKey = slugify(definition.displayName);
      const priorSuggestedType = toPascalCase(definition.key);
      definition.displayName = event.target.value;
      if (definition.key === priorSuggestedKey || definition.key === '') {
        definition.key = slugify(definition.displayName);
      }
      if (definition.csharpTypeName === priorSuggestedType || definition.csharpTypeName === '') {
        definition.csharpTypeName = toPascalCase(definition.key);
      }
    } else if (field === 'key') {
      const priorSuggestedType = toPascalCase(definition.key);
      definition.key = event.target.value;
      if (definition.csharpTypeName === priorSuggestedType || definition.csharpTypeName === '') {
        definition.csharpTypeName = toPascalCase(definition.key);
      }
    } else if (field === 'csharpTypeName') {
      definition.csharpTypeName = event.target.value;
    } else if (field === 'description') {
      definition.description = event.target.value;
    }
    props.onDocumentChange(document);
  }

  function addValue(): void {
    const document = cloneSchemaDocument(props.document);
    const index = findEnumIndex({ document }, selectedId);
    if (index < 0) {
      return;
    }
    document.enums[index]!.values.push(createEnumValue(document.enums[index]!.values));
    props.onDocumentChange(document);
  }

  function changeValueField(event: React.ChangeEvent<HTMLInputElement>): void {
    const document = cloneSchemaDocument(props.document);
    const enumIndex = findEnumIndex({ document }, selectedId);
    if (enumIndex < 0) {
      return;
    }
    const definition = document.enums[enumIndex]!;
    const valueIndex = findEnumValueIndex(definition.values, event.currentTarget.dataset.valueId ?? '');
    if (valueIndex < 0) {
      return;
    }
    const value = definition.values[valueIndex]!;
    const field = event.target.name;
    if (field === 'value') {
      value.value = parseNullableInteger(event.target.value);
    } else if (field === 'displayName') {
      const priorSuggestedKey = slugify(value.displayName);
      value.displayName = event.target.value;
      if (value.key === priorSuggestedKey || value.key === '') {
        value.key = slugify(value.displayName);
      }
    } else if (field === 'key') {
      value.key = event.target.value;
    } else if (field === 'description') {
      value.description = event.target.value;
    }
    props.onDocumentChange(document);
  }

  function deleteValue(event: React.MouseEvent<HTMLButtonElement>): void {
    const document = cloneSchemaDocument(props.document);
    const enumIndex = findEnumIndex({ document }, selectedId);
    if (enumIndex < 0) {
      return;
    }
    const values = document.enums[enumIndex]!.values;
    const valueIndex = findEnumValueIndex(values, event.currentTarget.dataset.valueId ?? '');
    if (valueIndex >= 0) {
      values.splice(valueIndex, 1);
      props.onDocumentChange(document);
    }
  }

  function deleteEnum(): void {
    if (!window.confirm('Delete this enum from the draft? Referencing properties will remain and must be repaired before publishing.')) {
      return;
    }
    const document = cloneSchemaDocument(props.document);
    const index = findEnumIndex({ document }, selectedId);
    if (index < 0) {
      return;
    }
    document.enums.splice(index, 1);
    props.onDocumentChange(document);
    setSelectedId(document.enums[index]?.id ?? document.enums[index - 1]?.id ?? '');
  }

  async function save(): Promise<void> {
    await props.onSave();
  }
}

function findEnumIndex(props: Pick<SchemaEditorCommonProps, 'document'>, id: string): number {
  for (let index = 0; index < props.document.enums.length; index += 1) {
    if (props.document.enums[index]!.id === id) {
      return index;
    }
  }
  return -1;
}

function findEnumValueIndex(values: Array<{ id: string }>, id: string): number {
  for (let index = 0; index < values.length; index += 1) {
    if (values[index]!.id === id) {
      return index;
    }
  }
  return -1;
}
